import { useEffect, useState } from "react";

/** COMPONENT */
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import { notiError } from "../../../component/notification/notifications";
import DeductTable from "../../depositRefund/components/DeductTable";
import ReceiptTable from "../../depositRefund/components/ReceiptTable";

/** API */
import StallReclaimApi, {
  StallReclaimFile,
  StallReclaimLog,
} from "../../../api/stall-reclaim/stall-reclaim.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  STALL_RECLAIM_LOG_ACTION_LABEL,
  STALL_RECLAIM_STATUS,
  stallReclaimStatusBg,
  stallReclaimStatusColor,
  stallReclaimStatusLabel,
} from "../../../constants/stallReclaim";
import { dateTimeToView } from "../../../utils/date.utils";
import { getApiErrorStatus } from "../../../utils/api-error.utils";
import { formatFileSize } from "../../../utils/common";
import {
  DASH,
  getDepositRefundError as getStallReclaimError,
} from "../../depositRefund/depositRefund.utils";
import {
  displayFileName,
  downloadStallReclaimFile,
  openStallReclaimFile,
} from "../stallReclaimFile.utils";

/** STYLE */
import * as Style from "../stallReclaim.style";

type Props = {
  id: number;
  /** ข้อความใต้หัวข้อ — ให้ caller ประกอบมาให้เลย (เลขที่คำสั่ง · ผู้เช่า · แผงค้า) */
  subTitle?: string;
  /** ผู้เช่า/แผงของคำสั่ง — ใช้ดึงตารางใบเสร็จในรายการที่อนุมัติ */
  userId: number;
  stallId?: number | null;
  onClose: () => void;
};

/** ไฟล์ที่กำลังโหลดอยู่ — เก็บ action ด้วยเพื่อให้ spinner ขึ้นตรงปุ่มที่กด */
type FileBusy = { id: number; action: "view" | "download" };

/**
 * modal นี้ชื่อ "ประวัติการพิจารณา" และดีไซน์แสดงเฉพาะรายการที่เป็นการพิจารณา/ลบ
 * จึงกรองแถว CREATE (สร้างคำสั่ง) ออก — backend ส่งมาทุกใบตั้งแต่วินาทีที่สร้าง
 * ถ้าไม่กรอง data จะไม่เคยว่าง แล้ว empty state "ยังไม่มีการพิจารณา" ไม่มีโอกาสแสดง
 *
 * ไม่มี CANCEL เหมือนเมนูคืนค่ามัดจำ เพราะเมนูนี้ไม่มีฝั่งลูกค้าให้ยกเลิกคำสั่ง
 */
const REVIEW_ACTIONS: StallReclaimLog["action"][] = ["REVIEW", "DELETE"];

export default function ModalStallReclaimLog({
  id,
  subTitle,
  userId,
  stallId,
  onClose,
}: Props) {
  const [logs, setLogs] = useState<StallReclaimLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<FileBusy | null>(null);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadLog = async () => {
      setIsLoading(true);
      try {
        const res = await StallReclaimApi.log(id);
        if (!isMounted) return;
        const rows = Array.isArray(res.data?.data) ? [...res.data.data] : [];
        setLogs(
          rows
            .filter((log) => REVIEW_ACTIONS.includes(log.action))
            // เรียงใหม่ -> เก่า เองด้วย ไม่พึ่ง order ที่ backend ส่งมา
            .sort((a, b) => b.id_deposit_refund_log - a.id_deposit_refund_log)
        );
      } catch (error) {
        if (!isMounted) return;
        setLogs([]);
        notiError(getStallReclaimError(error, "ดึงประวัติการพิจารณาไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadLog();
    return () => {
      isMounted = false;
    };
  }, [id]);

  /** 404 ของ backend ตอบ body ว่าง จึงต้องดูจาก status แล้วใส่ข้อความเอง */
  const notiFileError = (error: any, fallback: string) => {
    const status = getApiErrorStatus(error);
    notiError(status === 404 ? "ไม่พบไฟล์แนบที่ต้องการ" : getStallReclaimError(error, fallback));
  };

  const handleView = async (file: StallReclaimFile) => {
    if (busy) return;

    // ต้องเปิดแท็บตรงนี้ ตอนยังอยู่ใน user gesture ไม่งั้นโดน popup blocker หลัง await
    const tab = window.open("", "_blank");
    if (!tab) {
      notiError("เบราว์เซอร์บล็อกการเปิดแท็บใหม่ กรุณาอนุญาต popup หรือกดดาวน์โหลดแทน");
      return;
    }

    setBusy({ id: file.id_deposit_refund_file, action: "view" });
    try {
      await openStallReclaimFile(file, tab);
    } catch (error) {
      tab.close();
      notiFileError(error, "เปิดไฟล์แนบไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (file: StallReclaimFile) => {
    if (busy) return;

    setBusy({ id: file.id_deposit_refund_file, action: "download" });
    try {
      await downloadStallReclaimFile(file);
    } catch (error) {
      notiFileError(error, "ดาวน์โหลดไฟล์แนบไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const renderFile = (file: StallReclaimFile) => {
    const fileId = file.id_deposit_refund_file;
    const isViewing = busy?.id === fileId && busy?.action === "view";
    const isDownloading = busy?.id === fileId && busy?.action === "download";
    const isDisabled = !!busy;

    return (
      <Box
        key={fileId}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          px: 2,
          py: 1.5,
          borderRadius: "8px",
          border: `1px solid ${colors.lightGray}`,
          backgroundColor: colors.white,
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: colors.textLightGray }} />
        {/* ตัด timestamp ที่ backend เติมหน้าชื่อไฟล์ออก ผู้ใช้ไม่ต้องเห็น */}
        <Typography sx={{ fontSize: 16, flex: 1, minWidth: 0, wordBreak: "break-all" }}>
          {displayFileName(file.file_name)}
        </Typography>
        {file.file_size ? (
          <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
            {formatFileSize(file.file_size)}
          </Typography>
        ) : null}
        <Tooltip title="เปิดดูไฟล์">
          <span>
            <IconButton size="small" disabled={isDisabled} onClick={() => handleView(file)}>
              {isViewing ? (
                <CircularProgress size={16} sx={{ color: colors.themeMainColor }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ fontSize: 20, color: colors.textLightGray }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="ดาวน์โหลดไฟล์">
          <span>
            <IconButton size="small" disabled={isDisabled} onClick={() => handleDownload(file)}>
              {isDownloading ? (
                <CircularProgress size={16} sx={{ color: colors.themeMainColor }} />
              ) : (
                <DownloadOutlinedIcon sx={{ fontSize: 20, color: colors.themeMainColor }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  /** 1 รายการ = การ์ดหัวเรื่อง + การ์ดตารางคำนวณ + ตารางใบเสร็จ + กลุ่มไฟล์แนบ */
  const renderLog = (log: StallReclaimLog) => (
    <Box key={log.id_deposit_refund_log} sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          px: 2,
          py: 2,
          borderRadius: "8px",
          border: `1px solid ${colors.lightGray}`,
          backgroundColor: colors.white,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            // result เป็น null เมื่อ action ไม่ใช่ REVIEW (ลบคำสั่ง) ให้ใช้สีเทากลางๆ
            backgroundColor: stallReclaimStatusColor(log.result || ""),
            flexShrink: 0,
            mt: "7px",
          }}
        />
        <Box minWidth={0} sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                {STALL_RECLAIM_LOG_ACTION_LABEL[log.action] || log.action}
              </Typography>
              {log.result && (
                <Style.StatusChip
                  chipColor={stallReclaimStatusColor(log.result)}
                  chipBg={stallReclaimStatusBg(log.result)}
                  sx={{ fontSize: 14, minWidth: "unset", px: 1.5, py: "2px" }}
                >
                  {stallReclaimStatusLabel(log.result)}
                </Style.StatusChip>
              )}
            </Box>
            <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
              {/* action_date เป็น DATETIME จริง ใช้ timezone เครื่องได้ ไม่ต้อง force Asia/Bangkok */}
              {log.action_date ? dateTimeToView(log.action_date) : DASH}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 14, color: colors.textLightGray, mt: 0.5 }}>
            {`โดย ${log.action_name || DASH}`}
          </Typography>

          {log.note && (
            <Typography
              sx={{ fontSize: 14, whiteSpace: "pre-line", wordBreak: "break-word", mt: 0.5 }}
            >
              {log.note}
            </Typography>
          )}
        </Box>
      </Box>

      {/* deducts มีค่าเฉพาะ log ที่ action = REVIEW และ result = APPROVE */}
      {log.deducts?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <DeductTable deducts={log.deducts} approvedAmount={log.approved_amount} />
        </Box>
      )}

      {/* ใบเสร็จของผู้เช่า/แผงในคำสั่งนี้ — เฉพาะรายการที่อนุมัติ ตามดีไซน์ */}
      {log.result === STALL_RECLAIM_STATUS.APPROVE && (
        <Box sx={{ mt: 2 }}>
          <ReceiptTable userId={userId} stallId={stallId} />
        </Box>
      )}

      {log.files?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 16 }}>ไฟล์แนบจากเจ้าหน้าที่</Typography>
          {log.files.map(renderFile)}
        </Box>
      )}
    </Box>
  );

  const renderBody = () => (
    <Box
      className="custom-scroll"
      sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}
    >
      {subTitle && (
        <Typography sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 2 }}>
          {subTitle}
        </Typography>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
        </Box>
      ) : logs.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
            borderRadius: "8px",
            border: `1px solid ${colors.lightGray}`,
            backgroundColor: colors.white,
          }}
        >
          <Typography sx={{ color: colors.textLightGray }}>
            ยังไม่มีการพิจารณาคำสั่งนี้
          </Typography>
        </Box>
      ) : (
        // ตัด margin ล่างของการ์ดสุดท้ายออก ไม่ให้มีช่องว่างเกินก่อนขอบ modal
        <Box sx={{ "& > div:last-of-type": { mb: 0 } }}>{logs.map(renderLog)}</Box>
      )}
    </Box>
  );

  return (
    <ModalCustom
      // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
      isShowProps
      // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
      autoResetModal={false}
      size="lg"
      closeButton
      title="ประวัติการพิจารณา"
      component={renderBody()}
      // ไม่ส่ง onSubmit/textBtnConfirm footer จึงไม่มีปุ่ม เหลือแค่ X มุมขวาบนตามดีไซน์
      onClose={onClose}
    />
  );
}
